import React from 'react'
import { Box, Typography, Checkbox, Paper } from '@mui/material'
import { useReviewCorrections } from './hooks'
import { capitalize, getSanitizedDisplayValue } from './formatUtils'
import { PagedDisplay } from '../../PagedDisplay'
import type { CorrectionEntryItem } from './types'

export function ReviewCorrections() {
  const {
    processResult,
    applyStatus,
    correctionEntries,
    selectedCorrections,
    selectedCount,
    allSelected,
    someSelected,
    handleSelectAll,
    handleSelectCorrection,
  } = useReviewCorrections()

  if (!processResult) {
    return null
  }

  if (!processResult.results?.[0]?.result) {
    return (
      <Box border={1} borderColor="grey.300" borderRadius={1} p={2} mt={2}>
        <Typography variant="h6" gutterBottom>
          Review Corrections
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Process result data not available
        </Typography>
      </Box>
    )
  }

  if (correctionEntries.length === 0) {
    return (
      <Box border={1} borderColor="grey.300" borderRadius={1} p={2} mt={2}>
        <Typography variant="h6" gutterBottom>
          Review Corrections
        </Typography>
        <Typography variant="body2" color="text.secondary">
          No corrections to review
        </Typography>
      </Box>
    )
  }

  const renderCorrectionItem = (entryItem: CorrectionEntryItem) => (
    <Box key={entryItem.key}>
      <Box
        display="flex"
        alignItems="flex-start"
        py={0.5}
        px={1}
        sx={{
          borderBottom: '1px solid',
          borderColor: 'grey.200',
          '&:last-child': { borderBottom: 'none' },
        }}
      >
        <Checkbox
          checked={selectedCorrections.has(entryItem.key)}
          onChange={(e) =>
            handleSelectCorrection(entryItem.key, e.target.checked)
          }
          sx={{ mt: -0.5, mr: 1 }}
          size="small"
        />

        <Box flex={1} display="flex" gap={1}>
          <Box flex={1}>
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ mb: 0.25, display: 'block' }}
            >
              {capitalize(entryItem.fieldType)} -{' '}
              {entryItem.entry.node_ids.length} node
              {entryItem.entry.node_ids.length !== 1 ? 's' : ''} to be updated
            </Typography>
            <Paper
              variant="outlined"
              sx={{
                p: 0.5,
                backgroundColor: '#e3f2fd', // light blue
                color: 'text.primary',
                fontSize: '12px',
                fontFamily: 'Arial, sans-serif',
                wordBreak: 'break-word',
                '& *': {
                  margin: 0,
                  padding: 0,
                  fontFamily: 'Arial, sans-serif',
                  fontSize: '12px !important',
                  lineHeight: '1.2',
                },
                '& .media-placeholder': {
                  display: 'inline-block',
                  backgroundColor: 'rgba(0, 0, 0, 0.05)',
                  padding: '2px 4px',
                  borderRadius: '2px',
                  fontStyle: 'italic',
                  color: 'text.secondary',
                  fontSize: '11px',
                },
              }}
              dangerouslySetInnerHTML={{
                __html: getSanitizedDisplayValue(
                  entryItem.entry.orig_value,
                  entryItem.fieldType,
                ),
              }}
            />
          </Box>

          <Box flex={1}>
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ mb: 0.25, display: 'block' }}
            >
              Corrected
            </Typography>
            <Paper
              variant="outlined"
              sx={{
                p: 0.5,
                backgroundColor: '#e8f5e8', // light green
                color: 'text.primary',
                fontSize: '12px',
                fontFamily: 'Arial, sans-serif',
                wordBreak: 'break-word',
                '& *': {
                  margin: 0,
                  padding: 0,
                  fontFamily: 'Arial, sans-serif',
                  fontSize: '12px !important',
                  lineHeight: '1.2',
                },
                '& .media-placeholder': {
                  display: 'inline-block',
                  backgroundColor: 'rgba(0, 0, 0, 0.05)',
                  padding: '2px 4px',
                  borderRadius: '2px',
                  fontStyle: 'italic',
                  color: 'text.secondary',
                  fontSize: '11px',
                },
              }}
              dangerouslySetInnerHTML={{
                __html: getSanitizedDisplayValue(
                  entryItem.entry.corrected_value,
                  entryItem.fieldType,
                ),
              }}
            />
          </Box>
        </Box>
      </Box>
    </Box>
  )

  return (
    <Box
      border={1}
      borderColor="grey.300"
      borderRadius={1}
      p={2}
      mt={2}
      sx={{
        opacity: applyStatus ? 0.5 : 1,
        pointerEvents: applyStatus ? 'none' : 'auto',
      }}
    >
      <PagedDisplay
        items={correctionEntries}
        title="Review Corrections"
        renderItem={renderCorrectionItem}
        checkable
        allSelected={allSelected}
        someSelected={someSelected}
        onSelectAll={handleSelectAll}
        selectedCount={selectedCount}
        maxHeight={400}
        filterOptions={[
          { label: 'Title', value: 'title' },
          { label: 'Description', value: 'description' },
          { label: 'Body', value: 'body' },
        ]}
        getFilterValue={(item) => item.fieldType}
        filterLabel="Field Type"
      />
    </Box>
  )
}
